import React from 'react';
import { Container, Typography, Paper, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function MuiRhfAdvanced1Docs() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          MUI + RHF Advanced - Part 1
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          🏠 HOME
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        복잡한 폼 구조와 Controller를 마스터해보겠습니다. 
        다단계 폼, 동적 필드 배열, 조건부 렌더링 등 실무에서 자주 사용하는 고급 패턴들을 학습합니다.
      </Typography>

      <Typography variant="h4" gutterBottom sx={{ mt: 4, mb: 2 }}>
        복잡한 폼 구조와 Controller 마스터하기
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Controller가 왜 필요할까요?
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        일반적인 HTML input은 React Hook Form의 register로 바로 연결할 수 있지만, 
        MUI 컴포넌트들은 내부적으로 복잡한 구조를 가지고 있어서 직접 연결이 어렵습니다. 
        Controller는 이런 "제어되지 않는 컴포넌트"들을 React Hook Form과 연결해주는 역할을 합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`// 기본 Controller 사용법
import { Controller, useForm } from 'react-hook-form';
import { TextField, Select, MenuItem } from '@mui/material';

function ProductForm() {
  const { control, handleSubmit } = useForm();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="productName"
        control={control}
        defaultValue=""
        rules={{ required: '상품명은 필수입니다' }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label="상품명"
            error={!!error}
            helperText={error?.message}
            fullWidth
          />
        )}
      />
      
      <Controller
        name="category"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <Select
            {...field}
            displayEmpty
            fullWidth
          >
            <MenuItem value="">카테고리 선택</MenuItem>
            <MenuItem value="electronics">전자제품</MenuItem>
            <MenuItem value="clothing">의류</MenuItem>
          </Select>
        )}
      />
    </form>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        다단계 폼 (Stepper Form)
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        복잡한 폼을 여러 단계로 나누어 사용자 경험을 개선하는 방법입니다. 
        FormProvider를 사용하여 모든 단계에서 폼 상태를 공유할 수 있습니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Stepper, Step, StepLabel, Button, Box } from '@mui/material';

const steps = ['기본 정보', '상세 정보', '재고 관리', '확인'];

function MultiStepProductForm() {
  const [activeStep, setActiveStep] = useState(0);
  const methods = useForm({
    defaultValues: {
      // 1단계: 기본 정보
      name: '',
      description: '',
      category: '',
      
      // 2단계: 상세 정보
      price: 0,
      weight: 0,
      dimensions: { length: 0, width: 0, height: 0 },
      
      // 3단계: 재고 관리
      initialStock: 0,
      minStock: 0,
      reorderPoint: 0
    }
  });

  const { trigger, getValues } = methods;

  const handleNext = async () => {
    // 현재 단계 유효성 검사
    const fieldsToValidate = getFieldsForStep(activeStep);
    const isValid = await trigger(fieldsToValidate);
    
    if (isValid) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const onSubmit = (data) => {
    console.log('전체 폼 데이터:', data);
    // API 호출
  };

  return (
    <FormProvider {...methods}>
      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ mt: 3 }}>
          {activeStep === 0 && <BasicInfoStep />}
          {activeStep === 1 && <DetailedInfoStep />}
          {activeStep === 2 && <InventoryStep />}
          {activeStep === 3 && <ConfirmationStep />}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button 
            disabled={activeStep === 0} 
            onClick={handleBack}
          >
            이전
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button 
              variant="contained" 
              onClick={methods.handleSubmit(onSubmit)}
            >
              제출
            </Button>
          ) : (
            <Button 
              variant="contained" 
              onClick={handleNext}
            >
              다음
            </Button>
          )}
        </Box>
      </Box>
    </FormProvider>
  );
}

// 각 단계별 컴포넌트
function BasicInfoStep() {
  const { control } = useFormContext();
  
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Controller
          name="name"
          control={control}
          rules={{ required: '상품명은 필수입니다' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="상품명"
              fullWidth
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="상품 설명"
              multiline
              rows={4}
              fullWidth
            />
          )}
        />
      </Grid>
    </Grid>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        동적 필드 배열 (useFieldArray)
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        사용자가 필드를 동적으로 추가하거나 제거할 수 있는 기능입니다. 
        상품 옵션, 이미지 목록, 연락처 정보 등에 활용됩니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useFieldArray } from 'react-hook-form';
import { IconButton, Button } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

function ProductVariationsForm() {
  const { control } = useForm({
    defaultValues: {
      variations: [{ name: '', price: 0, stock: 0 }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "variations"
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        상품 변형 (사이즈, 색상 등)
      </Typography>
      
      {fields.map((field, index) => (
        <Paper key={field.id} sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Controller
                name={\`variations.\${index}.name\`}
                control={control}
                rules={{ required: '변형명은 필수입니다' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="변형명 (예: 빨강-L)"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Controller
                name={\`variations.\${index}.price\`}
                control={control}
                rules={{ 
                  required: '가격은 필수입니다',
                  min: { value: 0, message: '0 이상이어야 합니다' }
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="추가 금액"
                    type="number"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Controller
                name={\`variations.\${index}.stock\`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="재고 수량"
                    type="number"
                    fullWidth
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <IconButton
                onClick={() => remove(index)}
                disabled={fields.length === 1}
                color="error"
              >
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      ))}
      
      <Button
        startIcon={<Add />}
        onClick={() => append({ name: '', price: 0, stock: 0 })}
        variant="outlined"
      >
        변형 추가
      </Button>
    </Box>
  );
}`}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/docs/mui-rhf-advanced')}
        >
          ← MUI + RHF 개요
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/docs/mui-rhf-advanced-2')}
        >
          PART 2: 고급 컴포넌트 연동 →
        </Button>
      </Box>
    </Container>
  );
}
